import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { highnote } from "@highnote/server/src/sdk";

import { Ad } from "./AdModel";
import { AdsConfig, DEFAULT_ADS_CONFIG } from "./AdsConfig";
import { AdsResponse, parseAdsResponse } from "./AdsResponse";

type AdsContextValue = {
  /**
   * Ads to be interleaved into the comment carousel (sponsored +
   * brand-partner). Empty until the background fetch completes, or
   * permanently empty for paid subscribers.
   */
  carouselAds: Ad[];
  /**
   * Ads to render as pinned space items. Already capped by the server
   * at `config.spacePinnedMax`.
   */
  pinnedAds: Ad[];
  /**
   * Runtime-tunable ads configuration merged with safe defaults.
   */
  config: AdsConfig;
  /**
   * The space the provider is currently scoped to. Consumers plumb
   * this into analytics events so we don't have to thread `spaceId`
   * through every ad-related component tree manually.
   */
  spaceId: string | null;
  loading: boolean;
  /**
   * Error from the most recent fetch attempt. Retained for debugging;
   * the UI treats this as non-fatal — comments/items continue to
   * render regardless.
   */
  error: Error | null;
};

const DEFAULT: AdsContextValue = {
  carouselAds: [],
  pinnedAds: [],
  config: { ...DEFAULT_ADS_CONFIG },
  spaceId: null,
  loading: false,
  error: null,
};

const AdsContext = createContext<AdsContextValue>(DEFAULT);

type Props = {
  spaceId?: string | null;
  children: React.ReactNode;
  /**
   * Optional injection seam for tests. When provided, used in place of
   * `highnote.getAds` so unit tests can avoid mocking the entire SDK.
   * The returned value is parsed via `parseAdsResponse`, so callers
   * may safely return a partial shape in tests.
   */
  fetchAds?: (props: { spaceId?: string }) => Promise<unknown>;
};

/**
 * Fetches ads for the current user / space in the background and
 * exposes them via React context. Ads are fetched exactly once per
 * `spaceId` change; they are not persisted to localStorage or any
 * other client cache — ads are a fetch-per-session resource.
 *
 * Non-blocking by design: this provider never gates rendering on the
 * fetch. Consumers render comments/items immediately and fold ads in
 * when they arrive.
 */
export const AdsContextProvider = ({ spaceId, children, fetchAds }: Props) => {
  const [response, setResponse] = useState<AdsResponse>({
    carouselAds: [],
    pinnedAds: [],
    config: { ...DEFAULT_ADS_CONFIG },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const unmountedRef = useRef(false);

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const call = fetchAds ?? highnote.getAds;
    Promise.resolve(call({ spaceId: spaceId ?? undefined }))
      .then((raw) => {
        if (cancelled || unmountedRef.current) return;
        setResponse(parseAdsResponse(raw));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || unmountedRef.current) return;
        // Non-fatal: log and leave lists empty so comments render normally.
        console.warn("[ads] Failed to fetch ads:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setResponse({
          carouselAds: [],
          pinnedAds: [],
          config: { ...DEFAULT_ADS_CONFIG },
        });
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [spaceId, fetchAds]);

  const value = useMemo<AdsContextValue>(
    () => ({
      carouselAds: response.carouselAds,
      pinnedAds: response.pinnedAds,
      config: response.config,
      spaceId: spaceId ?? null,
      loading,
      error,
    }),
    [response, spaceId, loading, error],
  );

  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>;
};

export const useAdsContext = (): AdsContextValue => useContext(AdsContext);
